import { assert } from '@ember/debug';
import Service from '@ember/service';

/**
 * Holds the page still behind anything that overlays it.
 *
 * The lock itself is a single `overflow` value on `body`, so it cannot be taken twice. Claims are
 * tracked by owner instead: without that bookkeeping whoever releases last wins outright no matter
 * who is still holding, and closing the top of two stacked modals would scroll the page behind the
 * one still open.
 */
export default class BodyScrollManagerService extends Service {
  #bodyOverflow;

  #owners = new Set();

  /**
   * Claims the lock for `owner`, locking the page if it is not locked already.
   *
   * @param {string} owner - Id of the component claiming the lock.
   */
  lockScroll(owner) {
    assert('`lockScroll` requires the id of the component claiming the lock', owner);

    // Read on first claim rather than at construction, so what gets restored is whatever the page
    // was set to going into the lock rather than whatever it happened to be at app boot.
    if (!this.#owners.size) {
      this.#bodyOverflow = document.body.style.getPropertyValue('overflow');
    }

    this.#owners.add(owner);

    document.body.style.setProperty('overflow', 'hidden');
  }

  /**
   * Releases `owner`'s claim, restoring the page only once the last claim is gone.
   *
   * An owner that never claimed is ignored, so a caller can release unconditionally on teardown
   * without tracking whether it was the one that locked. That same early return is what keeps
   * `document` untouched under FastBoot, where nothing claimed in the first place.
   *
   * @param {string} owner - Id of the component releasing its claim.
   */
  unlockScroll(owner) {
    if (!this.#owners.delete(owner)) return;

    if (this.#owners.size) return;

    document.body.style.setProperty('overflow', this.#bodyOverflow);
  }
}
